import { Inject, Injectable } from '@angular/core';
import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions';
import { HttpClient } from '@angular/common/http';
import { Permission } from '../models/user.model';

export interface UserRole {
  name: string;
  permissions: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  /**
   * Espejo síncrono de los permisos cargados. `NgxPermissionsService.hasPermission`
   * es asíncrono y los guards y el menú necesitan decidir en el mismo tick.
   */
  private codigos = new Set<string>();

  constructor(
    @Inject(NgxPermissionsService) private permissionsService: NgxPermissionsService,
    @Inject(NgxRolesService) private rolesService: NgxRolesService,
    private http: HttpClient
  ) { }

  /**
   * Establece permisos directamente desde array plano (formato: "action.subject")
   * Usado cuando el backend ya unifica los permisos
   */
  setUserPermissions(permissions: Permission[]): void {
    this.permissionsService.flushPermissions();

    const uniquePermissions = [...new Set(permissions.map((p: Permission) => `${p.action}`))];

    this.codigos = new Set(uniquePermissions);
    this.permissionsService.loadPermissions(uniquePermissions);

    // console.log('✅ Permisos en ngx-permissions:', this.permissionsService.getPermissions());
  }

  /**
   * Establece roles y permisos del usuario (formato legacy con roles)
   * @deprecated Usar setUserPermissions() cuando el backend ya unifica permisos
   */
  setUserRolesAndPermissions(roles: UserRole[]): void {
    this.permissionsService.flushPermissions();
    this.rolesService.flushRoles();

    roles.forEach(role => {
      this.rolesService.addRole(role.name, role.permissions);
    });

    const allPermissions = roles.flatMap(role => role.permissions);
    const uniquePermissions = [...new Set(allPermissions)];

    this.permissionsService.loadPermissions(uniquePermissions);
  }

  /**
   * ✅ Limpia completamente permisos y roles
   */
  flushPermissions(): void {
    this.codigos.clear();
    this.permissionsService.flushPermissions();
    this.rolesService.flushRoles();
  }

  /** Consulta sin promesa, para guards y para armar el menú. */
  tienePermisoSincrono(permiso: string): boolean {
    return this.codigos.has(permiso);
  }

  tieneAlgunoSincrono(permisos: string[]): boolean {
    return permisos.some(p => this.codigos.has(p));
  }

  /** Todos los códigos cargados; cambia cada vez que se elige un rol. */
  codigosActuales(): ReadonlySet<string> {
    return this.codigos;
  }

  /**
   * Si el usuario tiene al menos un permiso del catálogo de módulos.
   * Cuando no tiene ninguno no se puede distinguir "sin acceso" de "el seeder
   * de roles todavía no corrió en este entorno", y el guard prefiere dejar
   * pasar antes que dejar la plataforma inutilizable.
   */
  tieneAlgunPermisoDeModulo(): boolean {
    for (const codigo of this.codigos) {
      if (codigo.endsWith('.access')) return true;
    }
    return false;
  }

  hasPermission(permission: string | string[]) {
    return this.permissionsService.hasPermission(permission);
  }

  async hasAnyPermission(permissions: string[]): Promise<boolean> {
    return await this.permissionsService.hasPermission(permissions);
  }

  hasRole(role: string): boolean {
    return this.rolesService.getRole(role) !== undefined;
  }

  getPermissions() {
    return this.permissionsService.getPermissions();
  }

  getRoles(): string[] {
    return Object.keys(this.rolesService.getRoles());
  }
}